import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import { ArrowForward } from '@mui/icons-material';
import { Skeleton, Tooltip } from '@mui/material';

import { VideaTypography } from '../../common-components/elements/VideaTypography/VideaTypography';
import { useGetTreatmentValues } from '../../react-query/useInsightsRecap';
import {
  blueBlack500,
  blueUltramarine070,
  green300,
  yellowOrange300,
} from '../../theme/videa-style-tokens';

import { Card, Flex } from './CommonStyles';
import { calculateHighlightedTreatment } from './utils';

const ArrowUpIcon = styled(ArrowForward)`
  background-color: ${green300};
  color: ${blueBlack500};
  transform: rotate(270deg);
  border-radius: 1.75em;
`;

const BarColumn = styled.div<{ height: number }>`
  background: ${green300};
  border-radius: 0.25em;
  width: 4em;
  &.pre-ai {
    background: ${yellowOrange300};
  }
  height: ${(props) => props.height}px;
`;

const treatmentTypeToCopyMap = {
  FILLINGS: 'Fillings',
  CROWNS: 'Crowns',
  SRP: 'SRP',
};

export const TreatmentValuesCard = () => {
  const { data, isLoading } = useGetTreatmentValues();

  if (isLoading) {
    return (
      <Card>
        <Skeleton variant="rectangular" height="2em" data-testid="skeleton" />
        <Flex className="center">
          <Skeleton
            variant="rectangular"
            height="3em"
            width="8em"
            data-testid="skeleton"
          />
          <Skeleton
            variant="circular"
            height="3em"
            width="3em"
            data-testid="skeleton"
          />
        </Flex>
        <Flex className="center">
          <Skeleton
            variant="rectangular"
            height="3em"
            width="8em"
            data-testid="skeleton"
          />
          <Skeleton
            variant="circular"
            height="3em"
            width="3em"
            data-testid="skeleton"
          />
        </Flex>
        <Flex className="center">
          <Skeleton
            variant="rectangular"
            height="3em"
            width="8em"
            data-testid="skeleton"
          />
          <Skeleton
            variant="circular"
            height="3em"
            width="3em"
            data-testid="skeleton"
          />
        </Flex>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  const highlightTreatment = calculateHighlightedTreatment(data);

  const preAIBaseline = data.preAi.find(
    (treatment) => treatment.treatmentType === highlightTreatment.treatmentType,
  )?.totalValuePerPatient;

  const numberOfPercentDecimals =
    highlightTreatment.treatmentPercentChange > 100 ? 0 : 1;
  const leftBarHeight =
    ((preAIBaseline || 0) / highlightTreatment.recapPeriodValuePerPatient) *
    100;

  // If no positive change, instead show the averages for all treatments
  if (highlightTreatment.treatmentPercentChange <= 0) {
    return (
      <Card className="padding-bottom">
        <VideaTypography variant="labelSmallStrong" color="white">
          <Trans>
            Average Treatment Values for the{' '}
            <Tooltip title="“Pre-AI baseline” data considers planned treatments (from your PMS) from the 3-month period prior to AI onboarding. “This month” considers planned treatments from the most recent full month of data">
              <VideaTypography variant="bodysmall" color={blueUltramarine070}>
                Month
              </VideaTypography>
            </Tooltip>
          </Trans>
        </VideaTypography>
        {data.recapPeriod.map((treatment) => (
          <Flex className="center" key={treatment.treatmentType}>
            <VideaTypography variant="bodyExtrasmall" color="white" width="9em">
              <Trans>
                Avg. {treatmentTypeToCopyMap[treatment.treatmentType]} Treatment
                Plan Value per Patient
              </Trans>
            </VideaTypography>
            <VideaTypography variant="headingMedium" color="white">
              ${treatment.totalValuePerPatient.toFixed(0)}
            </VideaTypography>
          </Flex>
        ))}
      </Card>
    );
  }

  return (
    <Card>
      <VideaTypography variant="labelSmallStrong" color="white">
        <Trans>
          Highlight of the Month:{' '}
          {treatmentTypeToCopyMap[highlightTreatment.treatmentType]} Treatment
          Value
        </Trans>
      </VideaTypography>
      <Flex className="center">
        <VideaTypography variant="bodyExtrasmall" color="white" width="9em">
          <Trans>
            Avg. {treatmentTypeToCopyMap[highlightTreatment.treatmentType]}{' '}
            Treatment Plan Value per Patient
          </Trans>
        </VideaTypography>
        <VideaTypography variant="headingMedium" color="white">
          ${highlightTreatment.recapPeriodValuePerPatient.toFixed(0)}
        </VideaTypography>
        {highlightTreatment.treatmentPercentChange > 0 && (
          <Flex className="column">
            <Flex className="center">
              <ArrowUpIcon />
              <VideaTypography variant="labelSmallStrong" color="white">
                <Trans>
                  UP{' '}
                  {highlightTreatment.treatmentPercentChange.toFixed(
                    numberOfPercentDecimals,
                  )}
                </Trans>
              </VideaTypography>
            </Flex>
          </Flex>
        )}
      </Flex>
      {highlightTreatment.treatmentPercentChange > 0 && (
        <VideaTypography
          variant="bodySmall"
          color="white"
          margin="2em"
          textAlign="center"
        >
          <Trans>
            Avg. {treatmentTypeToCopyMap[highlightTreatment.treatmentType]}{' '}
            treatment value per patient is up compared to pre-AI average value.
          </Trans>
        </VideaTypography>
      )}
      {preAIBaseline && highlightTreatment.recapPeriodValuePerPatient && (
        <Flex className="barCharts">
          <Flex className="barColumn">
            <BarColumn height={leftBarHeight} className="pre-ai" />
            <VideaTypography variant="bodyExtrasmall" color="white">
              <Trans>Pre-AI baseline</Trans>
            </VideaTypography>
            <VideaTypography variant="labelSmallStrong" color="white">
              ${preAIBaseline.toFixed(0)}
            </VideaTypography>
          </Flex>
          <Flex className="barColumn">
            <BarColumn height={100} />
            <VideaTypography variant="bodyExtrasmall" color="white">
              <Trans>This month</Trans>
            </VideaTypography>
            <VideaTypography variant="labelSmallStrong" color="white">
              ${highlightTreatment.recapPeriodValuePerPatient.toFixed(0)}
            </VideaTypography>
          </Flex>
        </Flex>
      )}
    </Card>
  );
};
